import { Component, OnInit, ViewChild, ElementRef } from '@angular/core';
import { MultiInputStepComponent } from '../../step/multi-input-step/multi-input-step.component';
import { AbstractControl, ControlContainer, FormGroupDirective, UntypedFormControl, ValidationErrors, Validators } from '@angular/forms';
import { environment } from '../../../../environments/environment';

@Component({
    selector: 'app-first-name-last-name',
    templateUrl: './first-name-last-name.component.html',
    styleUrls: ['./first-name-last-name.component.scss', '../../step/step.component.scss'],
    viewProviders: [{ provide: ControlContainer, useExisting: FormGroupDirective }],
    standalone: false
})
export class FirstNameLastNameComponent extends MultiInputStepComponent implements OnInit {
  
  @ViewChild('firstName') firstNameField: ElementRef;
  @ViewChild('lastName') lastNameField: ElementRef;
  public experiment: boolean;
  private cashoutAmount: number;
  private cashoutPercentage : number;
  private regex = /[^\p{L}\p{M} '`\-]/gu;
  public notDebtSettlement: boolean;
  public useUpdatedInputs: boolean = typeof window !== 'undefined'
    && new URLSearchParams(window.location.search).get('startAssistant') === '0';

  private normalizeName(value: any): string {
    return (value || '').toString().trim().toLowerCase().replace(/[^a-z]+/g, '');
  }

  private fakeFirstNameValidator = (control: AbstractControl): ValidationErrors | null => {
    const first = this.normalizeName(control.value);
    if (!first) { return null; }
    const blocked = ['test', 'fake', 'demo', 'sample', 'asdf', 'qwerty', 'none', 'unknown'];
    return blocked.indexOf(first) >= 0 ? { fakeName: true } : null;
  };

  private fakeLastNameValidator = (control: AbstractControl): ValidationErrors | null => {
    const last = this.normalizeName(control.value);
    if (!last) { return null; }
    const first = this.normalizeName(this.form && this.form.get('firstName') ? this.form.get('firstName').value : '');
    const blockedSingle = ['fake', 'demo', 'sample', 'asdf', 'qwerty', 'none', 'unknown'];
    const blockedCombos = ['testuser', 'testtest', 'fakename', 'johndoe', 'janedoe'];
    if (blockedSingle.indexOf(last) >= 0) { return { fakeName: true }; }
    if (first && blockedCombos.indexOf(first + last) >= 0) { return { fakeName: true }; }
    return null;
  };

  ngOnInit() {
    // First Name validation
    const firstNameControl = this.form.contains('firstName')
      ? this.form.get('firstName') as UntypedFormControl
      : new UntypedFormControl('');
    firstNameControl.setValidators([Validators.required, Validators.pattern(/^\s*[\p{L}\p{M} '`\-]+\s*$/u), Validators.minLength(2), this.fakeFirstNameValidator]);
    firstNameControl.updateValueAndValidity();
    this.controls.push(firstNameControl);
    // Last Name validation
    const lastNameControl = this.form.contains('lastName')
      ? this.form.get('lastName') as UntypedFormControl
      : new UntypedFormControl('');
    lastNameControl.setValidators([Validators.required, Validators.pattern(/^\s*[\p{L}\p{M} '`\-]+\s*$/u), Validators.minLength(2), this.fakeLastNameValidator]);
    lastNameControl.updateValueAndValidity();
    this.controls.push(lastNameControl);

    if (!this.form.contains('firstName')) { this.form.addControl('firstName', this.controls[0]); }
    if (!this.form.contains('lastName')) { this.form.addControl('lastName', this.controls[1]); }

    this.form.get('firstName').valueChanges.subscribe((value) => {
      const newVal = this.transform(value);
      this.form.controls.firstName.setValue(newVal, { emitEvent: false });
      this.form.controls.lastName.updateValueAndValidity({ emitEvent: false });
    });

    this.form.get('lastName').valueChanges.subscribe((value) => {
      const newVal = this.transform(value);
      this.form.controls.lastName.setValue(newVal, { emitEvent: false });
    });
  }

  initStep() {
    // Give first name focus
    setTimeout(() => this.firstNameField.nativeElement.focus(), 0);

    this.experiment = false; // TODO: migrate/remove 'vwo_personal_info_secure' test
    
    // check if notDebtSettlement
    this.notDebtSettlement = this.form.controls.formType.value != 'debtsettlement' && this.form.controls.formType.value != 'debtrelief';

    switch (this.form.value.maximumEquity) {
      case "yes": {
        this.cashoutPercentage = (this.form.controls.currentVA.value === 'yes' ? 1 : 0.9);
        this.cashoutAmount = this.form.controls.propertyValue.value * this.cashoutPercentage - 
                             this.form.controls.firstMortgageBalance.value;
        this.form.controls.cashOut.setValue(this.cashoutAmount);
      }
    }
    if (this.form.controls.formType.value == "homeimprovement") {
      this.form.controls.cashOutReason.setValue("home-improvement");
    }

    if (!environment.production && this.randomService.fillEnabled) {
      this.form.controls.firstName.setValue(this.randomService.consumer.firstName);
      this.form.controls.lastName.setValue(this.randomService.consumer.lastName);
    }
  }

  submit() {
    // trimming spaces in name and last name
    this.firstNameField.nativeElement.value = this.firstNameField.nativeElement.value.trim();
    this.lastNameField.nativeElement.value = this.lastNameField.nativeElement.value.trim();
    if (this.isFullFastFlow) {
      const fn = this.form.get('firstName'); const ln = this.form.get('lastName');
      if (fn) { fn.markAsTouched(); if (fn.invalid) { this.focusControl('firstName'); return; } }
      if (ln) { ln.markAsTouched(); if (ln.invalid) { this.focusControl('lastName'); return; } }
    }
    super.submit();
  }

  focusControl(name: string) {
    const els = Array.from(document.querySelectorAll('[formcontrolname="' + name + '"]')) as HTMLElement[];
    const el = els.find(e => (e as any).offsetParent !== null) || els[0];
    if (el) { setTimeout(() => el.focus(), 0); }
  }

  transform(result: string) {
    result = result.replace('’', '\'');
    result = result.replace(this.regex, '');

    //Removing any double symbols
    result = result.replace(/\s\s+/g, ' ');
    result = result.replace(/''+/g, '\'');
    result = result.replace(/``+/g, '\`');

    return result.replace(/--+/g, '-');
  }
}
